/**
 * Reusable client-side parser/validator for per-recipient schedule dates read
 * from a CSV cell. Salvaged from the (removed) bulk-import feature.
 *
 * Accepts:
 *   - ISO 8601 (e.g. `2026-12-25T09:00:00Z`, `2026-12-25T09:00`)
 *   - `MM/DD/YYYY HH:MM`   (e.g. `12/25/2026 09:00`)
 *   - `MM/DD/YYYY`         (e.g. `12/25/2026`, defaults to 00:00 local time)
 *   - `YYYY-MM-DD HH:MM`   (e.g. `2026-12-25 09:00`) — the CSV template format
 *   - `YYYY-MM-DD`
 *
 * Validates that the resulting date is parseable and in the future.
 * Returns epoch milliseconds on success.
 */
export interface ParseScheduleDateResult {
	valid: boolean;
	epochMs?: number;
	error?: string;
}

const MDY_TIME = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2}))?$/;
const YMD_TIME = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?$/;

export function parseScheduleDate(input: string): ParseScheduleDateResult {
	if (input == null) {
		return { valid: false, error: 'Empty date' };
	}

	const raw = String(input).trim();
	if (!raw) {
		return { valid: false, error: 'Empty date' };
	}

	let date: Date | null = null;

	// MM/DD/YYYY [HH:MM]
	const mdy = raw.match(MDY_TIME);
	if (mdy) {
		const [, month, day, year, hour, minute] = mdy;
		date = new Date(
			Number(year),
			Number(month) - 1,
			Number(day),
			hour ? Number(hour) : 0,
			minute ? Number(minute) : 0,
			0,
			0
		);
	}

	// YYYY-MM-DD [HH:MM] — treated as local time (no timezone suffix)
	if (!date) {
		const ymd = raw.match(YMD_TIME);
		if (ymd) {
			const [, year, month, day, hour, minute] = ymd;
			date = new Date(
				Number(year),
				Number(month) - 1,
				Number(day),
				hour ? Number(hour) : 0,
				minute ? Number(minute) : 0,
				0,
				0
			);
		}
	}

	// Fallback: native Date parsing (covers full ISO 8601 with timezone/seconds)
	if (!date) {
		const parsed = new Date(raw);
		if (!isNaN(parsed.getTime())) {
			date = parsed;
		}
	}

	if (!date || isNaN(date.getTime())) {
		return { valid: false, error: `Unrecognized date format: "${raw}"` };
	}

	const epochMs = date.getTime();
	if (epochMs <= Date.now()) {
		return { valid: false, error: `Date must be in the future: "${raw}"` };
	}

	return { valid: true, epochMs };
}

/**
 * CSV column names that are recognised as the per-recipient schedule date.
 * Matching is case-insensitive and ignores spaces/underscores.
 */
const SCHEDULE_DATE_ALIASES = ['scheduledate', 'scheduleddate', 'senddate'];

/**
 * Returns true if the given CSV column header refers to the schedule date.
 * Accepts `schedule_date`, `scheduled_date`, `send_date` (and space variants).
 */
export function isScheduleDateColumn(columnName: string): boolean {
	if (!columnName) return false;
	const normalized = String(columnName).toLowerCase().replace(/[\s_]+/g, '');
	return SCHEDULE_DATE_ALIASES.includes(normalized);
}

/**
 * Generates a downloadable CSV template for the schedule flow.
 * Columns: `name,email,schedule_date`. Any extra columns become merge tags.
 * Modelled on the (removed) bulk-import `downloadCsvTemplate`.
 */
export function downloadScheduleCsvTemplate(): void {
	const headers = 'name,email,schedule_date\n';
	const example = 'John Smith,john@example.com,2026-12-25 09:00\n';
	const note = '# Extra columns (e.g. company) become merge tags. Leave schedule_date blank to use the fallback date.\n';
	const blob = new Blob([headers + example + note], { type: 'text/csv;charset=utf-8;' });
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.setAttribute('download', 'cardclan-recipients-template.csv');
	document.body.appendChild(link);
	link.click();
	link.remove();
	window.URL.revokeObjectURL(url);
}
